import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { withChange } from '@shared/operators/with-change.operator';
import { getContribBiennaleControl } from '../validators/helpers/contrib-biennale-form.helpers';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { cloneDeep, isEqual } from 'lodash-es';
import { getControl, getFormArray } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { formPaths } from '@shared/constants/form-paths.const';

export const setContribBiennaleContributoListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_SociInseriti(form, store);
    my_CapitaleSociale(form, store);
    my_RicaviVendPrest(form, store);
    my_ContribDovuto(form, store);
    my_ContributoDovutoModif(form, store);
    my_dataCalcoloPenalita(form, store);
    my_DataVersamento(form, store);
    my_ScEdilObbl(form, store);
};

const my_SociInseriti = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getContribBiennaleControl(form, 'my_SociInseriti');
    control.valueChanges.pipe(withChangeDebounced(control)).subscribe(() => {
        pulisciContributoDovuto(form, store);
    });
};

const my_CapitaleSociale = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getContribBiennaleControl(form, 'my_CapitaleSociale');
    control.valueChanges.pipe(withChangeDebounced(control)).subscribe(() => {
        pulisciContributoDovuto(form, store);
    });
};

const my_RicaviVendPrest = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getContribBiennaleControl(form, 'my_RicaviVendPrest');
    control.valueChanges.pipe(withChangeDebounced(control)).subscribe(() => {
        pulisciContributoDovuto(form, store);
    });
};

// InfoPath (msoxd_my_ContributoDovutoModif_OnAfterChange): reagisce in modo sincrono, senza debounce.
// "Calcola Contributo"/"Calcola Penalità" sono azioni discrete a click: un debounce qui ritarderebbe
// la pulizia di ContribVersato oltre il click successivo, sovrascrivendo un calcolo già eseguito.
const my_ContribDovuto = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getContribBiennaleControl(form, 'my_ContribDovuto');
    control.valueChanges.pipe(withChange(control)).subscribe(() => {
        aggiornaMaggiorazioneEdilizie(form, store);
        pulisciContributoVersato(form, store);
    });
};

const my_ContributoDovutoModif = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getContribBiennaleControl(form, 'my_ContributoDovutoModif');
    control.valueChanges.pipe(withChange(control)).subscribe(() => {
        aggiornaMaggiorazioneEdilizie(form, store);
        pulisciContributoVersato(form, store);
    });
};

const my_ScEdilObbl = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getControl(form, `${formPaths.schedaCoopEdil}.my_ScEdilObbl`);
    control.valueChanges.pipe(withChangeDebounced(control)).subscribe(() => {
        aggiornaMaggiorazioneEdilizie(form, store);
    });
};

const my_dataCalcoloPenalita = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getControl(form, 'my_dataCalcoloPenalita');
    control.valueChanges.pipe(withChange(control)).subscribe(() => {
        pulisciContributoVersato(form, store);
    });
};

const my_DataVersamento = (form: FormGroup, store: Store<Revisione53State>): void => {
    const array = getFormArray(form, 'my_RTCalcoloMoraSanzione.my_RTRigaCalcoloMoraSanzione');
    // distinctUntilChanged di default confronta per riferimento: array.value è un array nuovo ad ogni
    // emissione (Angular lo ricostruisce sempre), quindi senza isEqual il confronto è un no-op e il
    // listener scatta anche quando il contenuto non è realmente cambiato (es. durante un populateForm
    // di refresh), azzerando my_ContribVersato senza motivo.
    array.valueChanges.pipe(withChange(array, isEqual)).subscribe(() => {
        pulisciContributoVersato(form, store);
    });
};

const pulisciContributoDovuto = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_ContribDovutoControl = getContribBiennaleControl(form, 'my_ContribDovuto');
    const my_ContributoDovutoModifControl = getContribBiennaleControl(form, 'my_ContributoDovutoModif');
    my_ContribDovutoControl?.setValue(0);
    my_ContributoDovutoModifControl?.patchValue('', { emitEvent: false });
    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
        const contrib = myVerbaleClone.my_SezioneI__Rilevazione.my_ContributoBiennale;
        contrib.my_ContribDovuto = my_ContribDovutoControl.value;
        contrib.my_ContributoDovutoModif = my_ContributoDovutoModifControl.value;
        return myVerbaleClone;
    });
};

const pulisciContributoVersato = (form: FormGroup, store: Store<Revisione53State>): void => {
    getContribBiennaleControl(form, 'my_ContribVersato')?.patchValue('', { emitEvent: false });
    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
        myVerbaleClone.my_SezioneI__Rilevazione.my_ContributoBiennale.my_ContribVersato = '';
        return myVerbaleClone;
    });
};

const aggiornaMaggiorazioneEdilizie = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_MaggEntiEdilDovutaControl = getContribBiennaleControl(form, 'my_MaggEntiEdilDovuta');
    // TODO aggiungi la condizione 'not XDocument.isSigned', non è applicabile al momento perchè manca la firma digitale
    const contributo = getContribBiennaleControl(form, 'my_ContributoDovutoModif');
    const valore = !isNilOrEmpty(contributo?.value) ? toNum(contributo.value) * 0.1 * toNum(getControl(form, `${formPaths.schedaCoopEdil}.my_ScEdilObbl`)?.value) : '';
    my_MaggEntiEdilDovutaControl.patchValue(valore, { emitEvent: false });
    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
        myVerbaleClone.my_SezioneI__Rilevazione.my_ContributoBiennale.my_MaggEntiEdilDovuta = my_MaggEntiEdilDovutaControl.value;
        return myVerbaleClone;
    });
};
